import type { HubspotSubmissionKind } from '../../../lib/hubspot-management/submissionKind';

export interface HubspotTicketReviewChecklistItem {
  id: string;
  title: string;
  guidance: string[];
}

export const hubspotTicketReviewChecklistItems: HubspotTicketReviewChecklistItem[] =
  [
    {
      id: 'documentation',
      title: 'Documentation and compliance pages checked',
      guidance: [
        'Open the privacy policy, terms, and any required developer or support documentation.',
        'Confirm the pages are public, user-facing, relevant, and do not resolve to homepages, blank pages, access-denied screens, or raw errors.',
      ],
    },
    {
      id: 'branding',
      title: 'Metadata, branding, and listing assets match',
      guidance: [
        'Compare the app label, package identity, screenshots, iconography, and listing copy.',
        'Make sure assets load correctly and nothing suggests impersonation, false affiliation, unauthorized trademark use, or other misleading claims.',
      ],
    },
    {
      id: 'build',
      title: 'Build, install flow, and package identity verified',
      guidance: [
        'Confirm the package looks like a production build and installs through the expected Android browser flow with default settings.',
        'Validate signing expectations and flag restricted, misleading, or continuity-breaking package naming for the release path being reviewed.',
      ],
    },
    {
      id: 'permissions',
      title: 'Permissions are necessary, justified, and disclosed',
      guidance: [
        'Inspect requested permissions and confirm they are required for core functionality.',
        'Flag sensitive or privileged permissions that are excessive, unjustified, or not clearly disclosed, especially on updates that add new access.',
      ],
    },
    {
      id: 'functionality',
      title: 'Core functionality works as described',
      guidance: [
        'Launch the dApp, confirm it reaches a usable state, and test the primary user flows described in the listing.',
        'Watch for startup or basic-use crashes, regressions in changed areas, unnecessary external redirects, and undisclosed external dependencies.',
      ],
    },
    {
      id: 'wallet-trust',
      title: 'Wallet, transaction, and trust checks completed',
      guidance: [
        'Review wallet connect and disconnect behavior, unavailable states, and transaction prompts, fees, outcomes, and error handling where applicable.',
        'Flag phishing, deceptive patterns, hidden functionality, unauthorized wallet usage, counterfeit asset behavior, or suspicious third-party dependencies.',
      ],
    },
  ];

export function getHubspotTicketReviewChecklistContext(
  submissionKind: HubspotSubmissionKind | null,
): string {
  if (submissionKind === 'new') {
    return 'New release focus: complete the full baseline review before changing status.';
  }

  if (submissionKind === 'update') {
    return 'Update focus: verify what changed, continuity, regressions, new permissions, and any new wallet or trust risk.';
  }

  return 'Complete the relevant review checks for this ticket before changing status.';
}
